import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import * as Icons from "lucide-react";
import {
  ArrowRight, Phone, MapPin, CheckCircle2, ChevronRight, Clock, Siren,
} from "lucide-react";
import { SITE, SERVICES } from "../lib/site";
import { SERVICE_CONTENT } from "../lib/serviceContent";
import { CITY_CONTENT, CITY_SLUGS } from "../lib/cityContent";
import QuoteForm from "../components/QuoteForm";

const Icon = ({ name, className }) => {
  const I = Icons[name] || Icons.Wrench;
  return <I className={className} />;
};

const BASE_URL = "https://alliancecraneservice.com";

export default function ServiceCity() {
  const { slug, citySlug } = useParams();
  const service = useMemo(() => SERVICES.find((s) => s.slug === slug), [slug]);
  const content = SERVICE_CONTENT[slug];
  const city = CITY_CONTENT[citySlug];

  if (!service || !content || !city) {
    return <Navigate to="/services" replace />;
  }

  const canonical = `${BASE_URL}/services/${slug}/${citySlug}`;
  const h1 = `${service.title} in ${city.name}, FL`;
  const metaTitle = `${service.title} in ${city.name}, FL — Alliance Crane Service`;
  const metaDescription = `${service.title} in ${city.name}, Florida (${city.county}). ${content.short} Certified operators, 40-ton & 50-ton mobile cranes, 24/7 emergency. Call ${SITE.phone}.`;

  // Sibling services in the same city
  const siblingServices = SERVICES.filter((s) => s.slug !== slug).slice(0, 6);
  // Same service in nearby cities
  const nearbyCities = (city.nearbyAreas || [])
    .map((c) => CITY_CONTENT[c]).filter(Boolean).slice(0, 4);

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: h1,
    serviceType: service.title,
    description: metaDescription,
    provider: {
      "@type": "LocalBusiness",
      "@id": `${BASE_URL}/#business`,
      name: SITE.name,
      telephone: SITE.phoneE164,
      email: SITE.email,
      address: {
        "@type": "PostalAddress",
        addressLocality: "DeLand",
        addressRegion: "FL",
        addressCountry: "US",
      },
    },
    areaServed: {
      "@type": "City",
      name: `${city.name}, FL`,
      containedInPlace: { "@type": "State", name: "Florida" },
    },
    url: canonical,
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Services", item: `${BASE_URL}/services` },
      { "@type": "ListItem", position: 3, name: service.title, item: `${BASE_URL}/services/${slug}` },
      { "@type": "ListItem", position: 4, name: city.name, item: canonical },
    ],
  };

  const cityFaq = city.faqs && city.faqs[0] ? city.faqs[0] : null;
  const combinedFaqs = [
    {
      q: `Do you provide ${service.title.toLowerCase()} in ${city.name}?`,
      a: `Yes. Alliance Crane Service provides ${service.title.toLowerCase()} throughout ${city.name} and ${city.county}, dispatched from our DeLand yard (${city.distance}, about ${city.driveTime}). Every job includes a certified operator and full insurance.`,
    },
    ...(content.faqs ? content.faqs.slice(0, 2) : []),
    ...(cityFaq ? [cityFaq] : []),
  ];

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: combinedFaqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <Helmet>
        <title>{`${metaTitle}`}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={`${service.title.toLowerCase()} ${city.name.toLowerCase()}, ${slug.replace(/-/g, " ")} ${city.name.toLowerCase()} fl, crane rental ${city.name.toLowerCase()}, ${content.keywords}`} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={content.heroImage} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content={`${city.name}, Florida`} />
        <meta name="geo.position" content={`${city.lat};${city.lng}`} />
        <script type="application/ld+json">{JSON.stringify(serviceLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[var(--ac-border)]">
        <div className="absolute inset-0">
          <img src={content.heroImage} alt={h1} className="w-full h-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 pt-12 md:pt-24 pb-16 md:pb-28">
          <nav
            data-testid="breadcrumbs"
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 md:gap-2 text-[11px] md:text-xs uppercase tracking-[0.14em] md:tracking-[0.18em] font-bold text-zinc-400 mb-6 md:mb-8 flex-wrap"
          >
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/services" className="hover:text-white">Services</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/services/${slug}`} className="hover:text-white">{service.title}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[var(--ac-red)]">{city.name}</span>
          </nav>

          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 border border-[var(--ac-border)] flex items-center justify-center bg-black/70">
              <Icon name={service.icon} className="w-5 h-5 text-[var(--ac-red)]" />
            </div>
            <span className="overline">{city.county} · Central Florida</span>
          </div>

          <h1
            data-testid="service-city-h1"
            className="font-display text-4xl sm:text-5xl lg:text-6xl max-w-4xl leading-[0.95]"
          >
            {service.title} in <span className="text-[var(--ac-red)]">{city.name}, Florida</span>
          </h1>

          <p className="mt-4 md:mt-5 text-base md:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            {content.intro} Serving {city.name} and all of {city.county} — {city.distance} from our DeLand yard.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Link to="/contact" className="btn-primary justify-center" data-testid="service-city-cta-quote">
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${SITE.phoneE164}`} className="btn-ghost justify-center" data-testid="service-city-cta-call">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="py-14 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-7">
            <div className="overline mb-3">{service.title} · {city.name}</div>
            <h2 className="font-display text-3xl md:text-4xl mb-5 leading-tight">
              {city.name}&apos;s crew for {service.title.toLowerCase()}.
            </h2>
            {content.body.map((p, i) => (
              <p key={i} className="text-zinc-300 leading-relaxed mb-5">{p}</p>
            ))}
            <p className="text-zinc-400 leading-relaxed mb-5">{city.coverage}</p>

            {content.applications && (
              <div className="mt-10">
                <div className="overline mb-4">What We Handle in {city.name}</div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {content.applications.map((a) => (
                    <li key={a} className="flex items-start gap-3 text-zinc-200">
                      <CheckCircle2 className="w-5 h-5 text-[var(--ac-red)] mt-0.5 shrink-0" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {city.neighborhoods && (
              <div className="mt-10">
                <div className="overline mb-4">{city.name} Neighborhoods & Corridors</div>
                <div className="flex flex-wrap gap-2">
                  {city.neighborhoods.map((n) => (
                    <span key={n} className="text-xs md:text-sm px-3 py-1.5 border border-[var(--ac-border)] bg-[var(--ac-bg-2)] text-zinc-300 font-mono-data tracking-wider">
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="md:col-span-5">
            <div className="surface p-6 md:p-7">
              <div className="overline mb-4">Why Alliance in {city.name}</div>
              <div className="space-y-5">
                {content.benefits.map((b) => (
                  <div key={b.title} className="border-l-2 border-[var(--ac-red)] pl-4">
                    <div className="font-display text-lg leading-tight">{b.title}</div>
                    <div className="text-sm text-zinc-400 mt-1 leading-relaxed">{b.body}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="surface p-6 md:p-7 mt-5">
              <div className="grid grid-cols-2 gap-px bg-[var(--ac-border)]">
                <div className="bg-[var(--ac-bg-2)] p-4">
                  <div className="overline !text-[10px]">Distance</div>
                  <div className="font-display text-xl mt-1">{city.distance}</div>
                </div>
                <div className="bg-[var(--ac-bg-2)] p-4">
                  <div className="overline !text-[10px]">Drive Time</div>
                  <div className="font-display text-xl mt-1">{city.driveTime}</div>
                </div>
              </div>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-center gap-2 text-zinc-300">
                  <Clock className="w-4 h-4 text-[var(--ac-red)]" /> {SITE.hours}
                </div>
                <div className="flex items-center gap-2 text-[var(--ac-red)] font-semibold">
                  <Siren className="w-4 h-4" /> {SITE.emergencyHours}
                </div>
              </div>
              <a href={`tel:${SITE.phoneE164}`} className="btn-primary w-full justify-center mt-6" data-testid="service-city-side-call">
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-24 bg-[var(--ac-bg-2)] border-y border-[var(--ac-border)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline mb-4">Questions</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95]">
              {city.name}<br /> FAQs.
            </h2>
          </div>
          <div className="md:col-span-8 border-t border-[var(--ac-border)]">
            {combinedFaqs.map((f, i) => (
              <details key={i} data-testid={`service-city-faq-${i}`} className="group border-b border-[var(--ac-border)] py-5">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <span className="font-display text-lg md:text-xl leading-snug">{f.q}</span>
                  <ChevronRight className="w-5 h-5 shrink-0 text-[var(--ac-red)] mt-1 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-zinc-400 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* INTERLINKS */}
      <section className="py-14 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          <div>
            <div className="overline mb-4">More Services in {city.name}</div>
            <div className="border-t border-l border-[var(--ac-border)]">
              {siblingServices.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}/${citySlug}`}
                  data-testid={`sibling-service-${s.slug}`}
                  className="hover-card-border border-b border-r border-[var(--ac-border)] px-4 py-3.5 flex items-center justify-between group bg-[var(--ac-bg)]"
                >
                  <span className="font-display text-lg">{s.title}</span>
                  <ChevronRight className="w-4 h-4 text-[var(--ac-red)] opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </div>
          <div>
            <div className="overline mb-4">{service.title} Nearby</div>
            <div className="border-t border-l border-[var(--ac-border)]">
              {nearbyCities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/services/${slug}/${c.slug}`}
                  data-testid={`nearby-city-${c.slug}`}
                  className="hover-card-border border-b border-r border-[var(--ac-border)] px-4 py-3.5 flex items-center justify-between group bg-[var(--ac-bg)]"
                >
                  <span className="font-display text-lg flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[var(--ac-red)]" /> {c.name}, FL
                  </span>
                  <ChevronRight className="w-4 h-4 text-[var(--ac-red)] opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
              <Link
                to={`/service-area/${citySlug}`}
                data-testid="service-city-view-city"
                className="hover-card-border border-b border-r border-[var(--ac-border)] px-4 py-3.5 flex items-center justify-between group bg-[var(--ac-bg)]"
              >
                <span className="font-display text-lg text-[var(--ac-red)]">All services in {city.name} →</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE CTA */}
      <section className="py-14 md:py-24 bg-black border-t border-[var(--ac-border)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-3 text-[var(--ac-red)]">Get Started</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95]">
              {service.title}<br /> in {city.name}?
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed max-w-md">
              Send the details and we&apos;ll get back fast with availability and pricing.
              Emergency? Call our 24/7 line.
            </p>
            <a href={`tel:${SITE.phoneE164}`} className="btn-primary mt-6" data-testid="service-city-bottom-call">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
          </div>
          <div className="lg:col-span-7">
            <QuoteForm />
          </div>
        </div>
      </section>
    </>
  );
}
